import { Button, ButtonGroup, Checkbox, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";

import {
  DashboardApi,
  DashboardSearch,
  DashboardTaskGridDto,
  EDepartment,
  ERequestType,
  ETaskType,
  RequestApi,
  UnassignedRequestApi,
} from "../../../api";
import { CustomDataTable, ExportButton } from "../../../components";
import { useGlobalData, useDialog } from "../../../contexts";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { getRouteStr } from "../../../utils/RequestRouteUtil";

export interface IDiplomaTaskListProps {
  searchUnassignedTitles: boolean;
}

export const DiplomaTaskList: React.FunctionComponent<IDiplomaTaskListProps> = ({ searchUnassignedTitles }) => {
  const api = useApiService(DashboardApi);
  const requestApi = useApiService(RequestApi);
  const unassignedRequestApi = useApiService(UnassignedRequestApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const { showDialog } = useDialog();
  const [sObjState, setsObjState] = React.useState<any>();
  const selectionList = React.useMemo(() => [], []);
  const closableIdsList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);

  const redirectToRequest = React.useCallback(
    async (requestId: number, requestType: ERequestType) => {
      const subRequestId = await requestApi.getSubRequestTypeId(requestId, requestType);
      const link = "#" + (await getRouteStr(requestType, subRequestId, "view"));
      const anchor: HTMLAnchorElement = document.createElement("a");
      anchor.href = link;
      anchor.target = "_blank";
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(link));
    },
    [requestApi]
  );

  const tableState = useGridState<any>({
    serverMode: true,
    availablePageSizes: [10],
    enablePagination: true,
    enableFilter: true,
    sortKeys: { requestNumber: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DashboardSearch) => {
      const s = DashboardSearch.fromJS({
        ...sObj,
        assigned: !searchUnassignedTitles,
        departementCode: EDepartment.Sport,
        languageId: currentLanguage.languageId,
        taskType: ETaskType.WorkbookRequestDiplomaCheckTask,
      });
      setsObjState(s);
      return api.baseSearch(s);
    },
    [api, currentLanguage.languageId, searchUnassignedTitles]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
  });

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: DashboardTaskGridDto) => {
      closableIdsList.push(row.requestId);
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.requestId)}
          onChange={() => {
            if (selectionList.includes(row.requestId)) {
              const index = selectionList.findIndex((e) => e === row.requestId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.requestId);
            }
          }}
        />
      );
    },
    [closableIdsList, selectionList]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
        render: (row: DashboardTaskGridDto) => {
          return (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a href="#" onClick={() => redirectToRequest(row.requestId, row.requestTypeCode)}>
              {row.requestNumber}
            </a>
          );
        },
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
    ],
    [columnCheckBox, redirectToRequest, t, toggleSelectAll]
  );

  const closeRequest = React.useCallback(
    async (requestList: number[]) => {
      if (requestList.length === 0) {
        showDialog({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.CloseTaskQuestion),
          title: t(ETLCodes.Close),
          onConfirmed: async () => {
            await api.closeMultiple(requestList);
            search();
          },
        });
      }
    },
    [showDialog, t, api, search]
  );

  const assignRequest = React.useCallback(
    async (requestList: number[]) => {
      if (requestList.length === 0) {
        showDialog({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.AssignElementsQuestion),
          title: t(ETLCodes.Assign),
          onConfirmed: async () => {
            await unassignedRequestApi.assignRequests(false, requestList);
            search();
          },
        });
      }
    },
    [unassignedRequestApi, search, showDialog, t]
  );

  return (
    <FieldSet
      title={t(ETLCodes.UnassignedRequests)}
      rightElement={
        <ButtonGroup>
          {searchUnassignedTitles ? (
            <Button
              text={t(ETLCodes.Assign)}
              onClick={() => assignRequest(selectionList)}
              intent={Intent.PRIMARY}
              minimal={true}
              icon={IconNames.TICK_CIRCLE}
            />
          ) : (
            <Button
              text={t(ETLCodes.Close)}
              onClick={() => closeRequest(selectionList)}
              intent={Intent.PRIMARY}
              minimal={true}
              icon={IconNames.TICK_CIRCLE}
            />
          )}

          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonGroup>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
    </FieldSet>
  );
};
